import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ComponentAccess } from '../../../../common/decorators/component-access.decorator';
import { AuthenticationService } from '../../../../common/services/authentication/authentication.service';
import { EmailGateService } from '../../../../common/services/email-gate/email-gate.service';
import { LogFactory } from '../../../../common/services/logger/log-factory';
import { LoadingMessage } from '../../../../common/enums/loading-message.enum';

/** Loose shape check only — the API's @IsEmail() is the authority. */
const EMAIL_SHAPE_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Blocking "Add your email" gate (ADR-0009 §6). Apple sign-in may supply no
 * email, leaving the account with a pending placeholder address. Marketplace
 * purchases and receipts flow via email, so this full-screen overlay blocks
 * the signed-in user until they provide a real address (then shows a
 * "check your inbox to verify" state). Not dismissible while pending.
 */
@Component({
  selector: 'app-email-gate',
  templateUrl: './email-gate.component.html',
  styleUrls: ['./email-gate.component.scss'],
  standalone: true,
  imports: [FormsModule],
})
@ComponentAccess('EmailGateComponent')
export class EmailGateComponent implements OnInit {
  /** Overlay is rendered (signed-in user has no usable email yet). */
  readonly visible = signal(false);
  /** Email saved — show the "check your inbox" state. */
  readonly awaitingVerification = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  /** Exposed for the template so the busy-button label reuses the shared enum. */
  protected readonly LoadingMessage = LoadingMessage;

  email = '';

  private readonly auth = inject(AuthenticationService);
  private readonly emailGate = inject(EmailGateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(LogFactory).createLogger(EmailGateComponent);

  ngOnInit(): void {
    this.auth
      .isAuthenticated()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          void this.evaluate();
        } else {
          this.visible.set(false);
          this.awaitingVerification.set(false);
          this.error.set(null);
          this.email = '';
        }
      });
  }

  get canSave(): boolean {
    return !this.saving() && EMAIL_SHAPE_REGEX.test(this.email.trim());
  }

  async save(): Promise<void> {
    if (!this.canSave) {
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    try {
      await this.emailGate.updateEmail(this.email.trim());
      this.awaitingVerification.set(true);
    } catch (err: unknown) {
      this.logger.warn('Email-gate save failed.', err);
      const httpError = err as {
        error?: { message?: string; error?: { message?: string } };
      };
      this.error.set(
        httpError?.error?.message ??
          httpError?.error?.error?.message ??
          'Could not save your email. Please try again.',
      );
    } finally {
      this.saving.set(false);
    }
  }

  /** Back from the "check your inbox" state to enter a different address. */
  useDifferentEmail(): void {
    this.awaitingVerification.set(false);
    this.error.set(null);
  }

  private async evaluate(): Promise<void> {
    const profile = await this.emailGate.getProfile();
    const pending = this.emailGate.isEmailPending(profile);
    this.visible.set(pending);
    if (!pending) {
      this.awaitingVerification.set(false);
    }
  }
}
